import React, { useMemo, useState } from "react";
import {
    Package,
    Plus,
    Search,
    Edit,
    Trash2,
    CheckCircle2,
    XCircle,
    RotateCcw,
} from "lucide-react";
import "../../styles/PackageTypes.css";

const initialPackageTypes = [
    {
        id: 1,
        name: "Basic Package",
        description: "Basic packing and moving services for small household moves.",
        services: 4,
        pricingType: "Fixed",
        price: 15000,
        status: "ACTIVE",
    },
    {
        id: 2,
        name: "Standard Package",
        description: "Standard packing, loading, transportation and unloading package.",
        services: 6,
        pricingType: "Fixed",
        price: 25000,
        status: "ACTIVE",
    },
    {
        id: 3,
        name: "Premium Package",
        description: "Complete moving package with packing, transportation and unpacking.",
        services: 8,
        pricingType: "Fixed",
        price: 40000,
        status: "ACTIVE",
    },
    {
        id: 4,
        name: "Economy Package",
        description: "Affordable moving package with essential transportation services.",
        services: 3,
        pricingType: "Fixed",
        price: 10000,
        status: "ACTIVE",
    },
    {
        id: 5,
        name: "Full Service Package",
        description: "End-to-end moving service including packing and unpacking.",
        services: 10,
        pricingType: "Variable",
        price: 50000,
        status: "ACTIVE",
    },
    {
        id: 6,
        name: "Office Move Package",
        description: "Specialized package for office and commercial relocation.",
        services: 7,
        pricingType: "Variable",
        price: 35000,
        status: "ACTIVE",
    },
    {
        id: 7,
        name: "Vehicle Move Package",
        description: "Dedicated transportation package for cars and two-wheelers.",
        services: 2,
        pricingType: "Fixed",
        price: 12000,
        status: "INACTIVE",
    },
    {
        id: 8,
        name: "Custom Package",
        description: "Customizable package based on customer requirements.",
        services: 0,
        pricingType: "Custom",
        price: 0,
        status: "ACTIVE",
    },
];

const formatPrice = (price, pricingType) => {
    if (pricingType === "Custom") {
        return "Custom";
    }

    if (!price) {
        return "—";
    }

    return `₹${price.toLocaleString("en-IN")}`;
};

export default function PackageTypes() {
    const [packages, setPackages] = useState(initialPackageTypes);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pricingFilter, setPricingFilter] = useState("");

    const [page, setPage] = useState(1);
    const rowsPerPage = 6;

    const filteredPackages = useMemo(() => {
        return packages.filter((item) => {
            const searchText = search.toLowerCase();

            const matchesSearch =
                item.name.toLowerCase().includes(searchText) ||
                item.description.toLowerCase().includes(searchText);

            const matchesStatus =
                !statusFilter || item.status === statusFilter;

            const matchesPricing =
                !pricingFilter || item.pricingType === pricingFilter;

            return matchesSearch && matchesStatus && matchesPricing;
        });
    }, [packages, search, statusFilter, pricingFilter]);

    const totalPages = Math.ceil(
        filteredPackages.length / rowsPerPage
    );

    const paginatedPackages = filteredPackages.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const totalPackages = packages.length;

    const activePackages = packages.filter(
        (item) => item.status === "ACTIVE"
    ).length;

    const inactivePackages = packages.filter(
        (item) => item.status === "INACTIVE"
    ).length;

    const totalServices = packages.reduce(
        (total, item) => total + item.services,
        0
    );

    const handleSearch = (value) => {
        setSearch(value);
        setPage(1);
    };

    const handleStatusFilter = (value) => {
        setStatusFilter(value);
        setPage(1);
    };

    const handlePricingFilter = (value) => {
        setPricingFilter(value);
        setPage(1);
    };

    const clearFilters = () => {
        setSearch("");
        setStatusFilter("");
        setPricingFilter("");
        setPage(1);
    };

    const handleAdd = () => {
        alert("Add Package Type form will be connected here.");
    };

    const handleEdit = (item) => {
        alert(`Edit Package Type: ${item.name}`);
    };

    const handleDelete = (id) => {
        const item = packages.find((pkg) => pkg.id === id);

        if (!item) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete "${item.name}"?`
        );

        if (!confirmed) return;

        setPackages((prev) =>
            prev.filter((pkg) => pkg.id !== id)
        );
    };

    const toggleStatus = (id) => {
        setPackages((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        status:
                            item.status === "ACTIVE"
                                ? "INACTIVE"
                                : "ACTIVE",
                    }
                    : item
            )
        );
    };

    return (
        <div className="package-types-page">

            {/* Header */}
            <div className="package-types-header">
                <div className="package-types-title">
                    <div className="package-types-title-icon">
                        <Package size={24} />
                    </div>

                    <div>
                        <h1>Package Types</h1>
                        <p>
                            Manage moving packages and their pricing details.
                        </p>
                    </div>
                </div>

                <button
                    className="package-types-primary-btn"
                    onClick={handleAdd}
                >
                    <Plus size={18} />
                    Add Package
                </button>
            </div>

            {/* Summary */}
            <div className="package-types-summary">

                <div className="package-types-summary-card">
                    <div className="package-types-summary-icon total">
                        <Package size={21} />
                    </div>

                    <div>
                        <span>Total Packages</span>
                        <strong>{totalPackages}</strong>
                    </div>
                </div>

                <div className="package-types-summary-card">
                    <div className="package-types-summary-icon active">
                        <CheckCircle2 size={21} />
                    </div>

                    <div>
                        <span>Active Packages</span>
                        <strong>{activePackages}</strong>
                    </div>
                </div>

                <div className="package-types-summary-card">
                    <div className="package-types-summary-icon services">
                        <Package size={21} />
                    </div>

                    <div>
                        <span>Total Services</span>
                        <strong>{totalServices}</strong>
                    </div>
                </div>

                <div className="package-types-summary-card">
                    <div className="package-types-summary-icon inactive">
                        <XCircle size={21} />
                    </div>

                    <div>
                        <span>Inactive Packages</span>
                        <strong>{inactivePackages}</strong>
                    </div>
                </div>

            </div>

            {/* Filters */}
            <div className="package-types-filter-card">

                <div className="package-types-search">
                    <Search size={18} />

                    <input
                        type="text"
                        placeholder="Search package name or description..."
                        value={search}
                        onChange={(e) =>
                            handleSearch(e.target.value)
                        }
                    />
                </div>

                <select
                    value={pricingFilter}
                    onChange={(e) =>
                        handlePricingFilter(e.target.value)
                    }
                >
                    <option value="">All Pricing Types</option>
                    <option value="Fixed">Fixed</option>
                    <option value="Variable">Variable</option>
                    <option value="Custom">Custom</option>
                </select>

                <select
                    value={statusFilter}
                    onChange={(e) =>
                        handleStatusFilter(e.target.value)
                    }
                >
                    <option value="">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                </select>

                <button
                    className="package-types-clear-btn"
                    onClick={clearFilters}
                >
                    <RotateCcw size={16} />
                    Clear
                </button>

            </div>

            {/* Desktop Table */}
            <div className="package-types-table-card">

                <div className="package-types-table-wrapper">

                    <table className="package-types-table">

                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Package</th>
                                <th>Description</th>
                                <th>Services</th>
                                <th>Pricing</th>
                                <th>Price</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>

                            {paginatedPackages.length > 0 ? (
                                paginatedPackages.map((item, index) => (
                                    <tr key={item.id}>

                                        <td>
                                            {(page - 1) * rowsPerPage +
                                                index +
                                                1}
                                        </td>

                                        <td>
                                            <div className="package-name-cell">
                                                <div className="package-name-icon">
                                                    <Package size={17} />
                                                </div>

                                                <strong>
                                                    {item.name}
                                                </strong>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="package-description">
                                                {item.description}
                                            </div>
                                        </td>

                                        <td>
                                            <span className="service-count">
                                                {item.services}
                                            </span>
                                        </td>

                                        <td>
                                            <span className="pricing-badge">
                                                {item.pricingType}
                                            </span>
                                        </td>

                                        <td>
                                            <strong className="package-price">
                                                {formatPrice(
                                                    item.price,
                                                    item.pricingType
                                                )}
                                            </strong>
                                        </td>

                                        <td>
                                            <button
                                                className={`package-status ${
                                                    item.status === "ACTIVE"
                                                        ? "active"
                                                        : "inactive"
                                                }`}
                                                onClick={() =>
                                                    toggleStatus(item.id)
                                                }
                                            >
                                                {item.status === "ACTIVE"
                                                    ? "Active"
                                                    : "Inactive"}
                                            </button>
                                        </td>

                                        <td>
                                            <div className="package-actions">

                                                <button
                                                    className="package-action edit"
                                                    onClick={() =>
                                                        handleEdit(item)
                                                    }
                                                    title="Edit"
                                                >
                                                    <Edit size={16} />
                                                </button>

                                                <button
                                                    className="package-action delete"
                                                    onClick={() =>
                                                        handleDelete(item.id)
                                                    }
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>

                                            </div>
                                        </td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="8"
                                        className="package-empty"
                                    >
                                        No package types found.
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>

                {/* Mobile Cards */}
                <div className="package-mobile-list">

                    {paginatedPackages.length > 0 ? (
                        paginatedPackages.map((item, index) => (
                            <div
                                className="package-mobile-card"
                                key={item.id}
                            >

                                <div className="package-mobile-top">

                                    <div className="package-mobile-title">

                                        <div className="package-name-icon">
                                            <Package size={17} />
                                        </div>

                                        <div>
                                            <strong>{item.name}</strong>
                                            <span>
                                                #
                                                {String(
                                                    (page - 1) *
                                                        rowsPerPage +
                                                        index +
                                                        1
                                                ).padStart(2, "0")}
                                            </span>
                                        </div>

                                    </div>

                                    <button
                                        className={`package-status ${
                                            item.status === "ACTIVE"
                                                ? "active"
                                                : "inactive"
                                        }`}
                                        onClick={() =>
                                            toggleStatus(item.id)
                                        }
                                    >
                                        {item.status === "ACTIVE"
                                            ? "Active"
                                            : "Inactive"}
                                    </button>

                                </div>

                                <p className="package-mobile-description">
                                    {item.description}
                                </p>

                                <div className="package-mobile-details">

                                    <div>
                                        <span>Services</span>
                                        <strong>
                                            {item.services}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>Pricing</span>
                                        <strong>
                                            {item.pricingType}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>Price</span>
                                        <strong>
                                            {formatPrice(
                                                item.price,
                                                item.pricingType
                                            )}
                                        </strong>
                                    </div>

                                </div>

                                <div className="package-mobile-actions">

                                    <button
                                        className="package-mobile-edit"
                                        onClick={() =>
                                            handleEdit(item)
                                        }
                                    >
                                        <Edit size={16} />
                                        Edit
                                    </button>

                                    <button
                                        className="package-mobile-delete"
                                        onClick={() =>
                                            handleDelete(item.id)
                                        }
                                    >
                                        <Trash2 size={16} />
                                        Delete
                                    </button>

                                </div>

                            </div>
                        ))
                    ) : (
                        <div className="package-mobile-empty">
                            No package types found.
                        </div>
                    )}

                </div>

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="package-types-pagination">

                        <span>
                            Showing{" "}
                            {Math.min(
                                (page - 1) * rowsPerPage + 1,
                                filteredPackages.length
                            )}{" "}
                            to{" "}
                            {Math.min(
                                page * rowsPerPage,
                                filteredPackages.length
                            )}{" "}
                            of {filteredPackages.length}
                        </span>

                        <div className="package-pagination-buttons">

                            <button
                                disabled={page === 1}
                                onClick={() =>
                                    setPage((prev) => prev - 1)
                                }
                            >
                                Previous
                            </button>

                            {Array.from(
                                { length: totalPages },
                                (_, index) => index + 1
                            ).map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    className={
                                        page === pageNumber
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setPage(pageNumber)
                                    }
                                >
                                    {pageNumber}
                                </button>
                            ))}

                            <button
                                disabled={page === totalPages}
                                onClick={() =>
                                    setPage((prev) => prev + 1)
                                }
                            >
                                Next
                            </button>

                        </div>

                    </div>
                )}

            </div>

        </div>
    );
}